import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { ISelectionTabProps } from "../../properties/TabProperties";

// SelectionTab — the app's single tab primitive: a frosted strip tray whose
// active indicator slides + briefly expands between tabs ("morph", a faithful
// port of the design DS pf-tabs--morph). Styling: tab-mt (tabStyles →
// libraryStyles). API: optionList=[{id, title, count?}] + selectedId +
// handleSelectOnClick. Legacy color props are accepted but ignored (the morph
// tray defines its own active styling). SelectionTabPill / SelectionTabFill
// delegate here so every selection tab in the app renders this morph style.
const SelectionTab = ({
  optionList,
  selectedId,
  handleSelectOnClick,
}: ISelectionTabProps) => {
  const listRef = useRef<HTMLDivElement | null>(null);
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [indicator, setIndicator] = useState<{
    left: number;
    width: number;
  } | null>(null);
  const [morphing, setMorphing] = useState(false);

  const measure = useCallback(() => {
    const list = listRef.current;
    const el = tabRefs.current[selectedId];
    if (!list || !el) return;
    const lr = list.getBoundingClientRect();
    const tr = el.getBoundingClientRect();
    setIndicator({ left: tr.left - lr.left + list.scrollLeft, width: tr.width });
  }, [selectedId]);

  useLayoutEffect(() => {
    measure();
    const ro = new ResizeObserver(measure);
    if (listRef.current) ro.observe(listRef.current);
    Object.values(tabRefs.current).forEach((el) => el && ro.observe(el));
    return () => ro.disconnect();
  }, [measure, optionList]);

  useEffect(() => {
    setMorphing(true);
    const id = setTimeout(() => setMorphing(false), 280);
    return () => clearTimeout(id);
  }, [selectedId]);

  return (
    <div className="tab-mt" role="tablist" ref={listRef}>
      {indicator ? (
        <>
          <span
            className="tab-mt__glow"
            style={{ left: indicator.left, width: indicator.width }}
            aria-hidden="true"
          />
          <span
            className={`tab-mt__ind${morphing ? " is-morphing" : ""}`}
            style={{ left: indicator.left, width: indicator.width }}
            aria-hidden="true"
          />
        </>
      ) : null}
      {optionList.map((option: any) => (
        <button
          key={option.id}
          role="tab"
          ref={(el) => {
            tabRefs.current[option.id] = el;
          }}
          aria-selected={selectedId === option.id}
          className={`tab-mt-btn${selectedId === option.id ? " is-active" : ""}`}
          onClick={() => handleSelectOnClick(option.id)}
        >
          {option.title}
          {option.count != null ? (
            <span className="tab-mt-count">{option.count}</span>
          ) : null}
        </button>
      ))}
    </div>
  );
};

export default SelectionTab;
